import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppStateFacade } from '@app-state';
import {
    PageMetadata,
    PageMetadataRegistry,
    PageScope,
} from '@app/router';
import { IFolder } from '@models';
import { TranslateService } from '@ngx-translate/core';
import { decodeBase64UTF16 } from '@utils/encode.utils';
import { MONITORING_FQN } from '@utils/queryParam.utils';
import { filterNullOrUndefined } from '@utils/rxjs.utils';

import flatMap from 'lodash-es/flatMap';
import {
    BehaviorSubject,
    combineLatest,
    Observable,
} from 'rxjs';
import {
    map,
    startWith,
    tap,
} from 'rxjs/operators';

interface IBreadcrumb {
    type: 'punctuation' | 'text';
    value: string;
}

@Component({
    selector: 'ui-breadcrumbs',
    templateUrl: './ui-breadcrumbs.component.html',
    styleUrls: ['./ui-breadcrumbs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiBreadCrumbsComponent {
    public PageScope = PageScope;
    public pageScopeIcon$ = new BehaviorSubject(PageScope.Global);
    public breadcrumbs$: Observable<IBreadcrumb[]> = combineLatest([
        this._appState.user.folder.value$,
        this._pageRegistry.activePage$.pipe(
            filterNullOrUndefined(),
            tap(p => this.pageScopeIcon$.next(p.scope)),
        ),
        this._route.queryParamMap.pipe(
            map(route => route.get(MONITORING_FQN)),
            map(fqn => fqn ? decodeBase64UTF16(fqn) : undefined),
        ),
        this._appState.language.value$,
    ]).pipe(
        map(([folder, activePage, fqnParam]) => {
            const folderPath = fqnParam
                ? this._extractPathFromString(fqnParam)
                : this._extractFolderPath(folder);

            const routePath = this.extractRoutePath(activePage);

            if (!folderPath.length) {
            } else if (routePath.length) {
                folderPath[folderPath.length - 1].value += ':';
            }

            return [...folderPath, ...routePath];
        }),
        startWith([]),
    );

    public tooltip$ = this.breadcrumbs$.pipe(
        map(breadcrumbs => breadcrumbs.map(b => b.value).join(' ')),
    );

    constructor(
        private _translateService: TranslateService,
        private _appState: AppStateFacade,
        private _pageRegistry: PageMetadataRegistry,
        private _route: ActivatedRoute,
    ) { }

    public extractRoutePath(page: PageMetadata) {
        if (!page.translationKey) {
            return [];
        }

        const path: IBreadcrumb[] = [];
        while (true) {
            path.push({
                type: 'text',
                value: this._translateService.instant(page.translationKey),
            });
            if (!page.parent) {
                break;
            }
            path.push({ type: 'punctuation', value: '>' });
            page = page.parent;
        }
        return path.reverse();
    }

    private _extractFolderPath(f: IFolder | null): IBreadcrumb[] {
        if (!this._appState.user.folder.hasAny || this._pageRegistry.activePage!.scope === PageScope.Global) {
            return [];
        }

        return this._extractPathFromString(
            f!.fullyQualifiedName,
        );
    }

    private _extractPathFromString(path: string): IBreadcrumb[] {
        return flatMap(
            path.split('/'),
            value => [{ type: 'punctuation', value: '/' }, { type: 'text', value }],
        ).slice(1) as IBreadcrumb[];
    }
}
